import api from '../api';
import { getBackoffDelay } from './backoff';
import type { TaskResponse } from './types';

const COMPLETED_STATUSES = ['completed', 'image_generated', 'video_generated', 'audio_generated', 'media_merged', 'analysis_completed'];
const FAILED_STATUSES = ['failed', 'generation_failed', 'video_extension_failed'];

// Polling Helper with exponential backoff (2s → 4s → 8s cap; ~16 min total at
// the default maxAttempts=120, since delays cap at 8s).
// Backward compatible: interval param is accepted but ignored in favor of backoff
const pollTask = async (
    taskId: number,
    _interval = 2000,
    maxAttempts = 120,
    onStatus?: (status: string) => void,
): Promise<TaskResponse> => {
    // Global wall-clock budget — even if we keep pausing for maintenance,
    // never spin forever. Covers the worst case where a deploy hangs or a
    // backend bug leaves the app in maintenance mode indefinitely.
    const MAINTENANCE_WAIT_BUDGET_MS = 10 * 60 * 1000; // 10 min
    let maintenanceWaitedMs = 0;

    for (let i = 0; i < maxAttempts; i++) {
        try {
            const response = await api.get(`/tasks/${taskId}`);
            const task = (response.data as any).data as TaskResponse;

            // Surface interim status to the caller (e.g. for a progress indicator).
            if (onStatus && task.status) onStatus(task.status);

            if (COMPLETED_STATUSES.includes(task.status)) {
                // Race condition fix: for brandbook/moodboard tasks, if brandbookId is missing, do extra poll
                const analysisType = (task.metadata as any)?.analysisType || (task.analysisResults as any)?.analysisType;
                const hasBrandbookId = !!(task as any).brandbookId || !!(task.metadata as any)?.brandbookId;

                if (['brandbook', 'moodboard'].includes(analysisType) && !hasBrandbookId) {
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    const retryResponse = await api.get(`/tasks/${taskId}`);
                    return (retryResponse.data as any).data as TaskResponse;
                }

                return task;
            }

            if (FAILED_STATUSES.includes(task.status)) {
                // Marcado para não cair no retry abaixo: a mensagem do backend
                // (ex.: créditos insuficientes) não contém "Task failed".
                throw Object.assign(new Error(task.errorMessage || 'Task failed'), { isTaskFailure: true });
            }
        } catch (e: any) {
            // Task failure errors must propagate immediately (not be retried)
            if (e?.isTaskFailure || e.message?.includes('Task failed') || e.message?.includes('No images were generated')) {
                throw e;
            }
            // Deploy/maintenance window: don't consume an attempt, sleep using Retry-After,
            // but respect a hard wall-clock budget so a stuck backend can't spin forever.
            if (e?.isMaintenance) {
                if (maintenanceWaitedMs >= MAINTENANCE_WAIT_BUDGET_MS) {
                    throw new Error('Deploy/maintenance prolonged beyond 10 minutes; aborting poll');
                }
                const backoff = Math.max(2000, (e.retryAfterSeconds || 30) * 1000);
                maintenanceWaitedMs += backoff;
                console.info(`Polling paused — deploy/maintenance in progress. Retrying in ${backoff}ms (total paused ${maintenanceWaitedMs}ms).`);
                await new Promise(resolve => setTimeout(resolve, backoff));
                i--; // don't count this iteration against maxAttempts
                continue;
            }
            // Network/transient errors: retry until maxAttempts
            console.warn(`Polling attempt ${i + 1}/${maxAttempts} failed:`, e.message);
            if (i === maxAttempts - 1) throw e; // Throw on last attempt
        }

        // Exponential backoff instead of fixed interval (PERF-15: 2s → 4s → 8s cap)
        await new Promise(resolve => setTimeout(resolve, getBackoffDelay(i)));
    }

    throw new Error("Task polling timed out.");
};

// Listen to Task Updates (Reverb/WebSocket) with polling fallback
const listenToTask = async (taskId: number, timeoutMs = 300000): Promise<TaskResponse> => {
    const echo = await (await import('../../lib/echo')).default;
    const humps = (await import('humps')).default;

    return new Promise((resolve, reject) => {
        let settled = false;
        let pollTimer: ReturnType<typeof setTimeout> | null = null;

        const cleanup = () => {
            try { echo.leave(`task.${taskId}`); } catch { /* noop */ }
            if (pollTimer) {
                clearTimeout(pollTimer);
                pollTimer = null;
            }
            clearTimeout(timeout);
        };

        const onResolve = (result: TaskResponse) => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(result);
        };

        const onReject = (err: Error) => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(err);
        };

        const timeout = setTimeout(() => {
            onReject(new Error('Task timed out waiting for update'));
        }, timeoutMs);

        // WebSocket subscription
        echo.private(`task.${taskId}`)
            .listen('.TaskStatusUpdated', (e: any) => {
                if (settled) return;
                // Camelize event data to match frontend expectations
                const event = humps.camelizeKeys(e) as TaskResponse;

                if (COMPLETED_STATUSES.includes(event.status)) {
                    // Fetch full task details to ensure we have all metadata
                    api.get(`/tasks/${taskId}`).then(res => {
                        onResolve((res.data as any).data as TaskResponse);
                    }).catch(() => {
                        onResolve(event);
                    });
                } else if (FAILED_STATUSES.includes(event.status)) {
                    onReject(new Error(event.errorMessage || 'Task failed'));
                }
            });

        // Polling fallback (runs in parallel, resolves first wins).
        // PERF-15: recursive setTimeout with exponential backoff (2s → 4s → 8s cap)
        // instead of a fixed 2s setInterval, to ease pressure on the php-fpm pool.
        let pollAttempt = 0;
        const scheduleNextPoll = () => {
            if (settled) return;
            pollTimer = setTimeout(async () => {
                if (settled) return;
                try {
                    const res = await api.get(`/tasks/${taskId}`);
                    const task = (res.data as any).data as TaskResponse;
                    if (COMPLETED_STATUSES.includes(task.status)) {
                        onResolve(task);
                        return;
                    }
                    if (FAILED_STATUSES.includes(task.status)) {
                        onReject(new Error(task.errorMessage || 'Task failed'));
                        return;
                    }
                } catch {
                    // Ignore transient polling errors
                }
                scheduleNextPoll();
            }, getBackoffDelay(pollAttempt++));
        };
        scheduleNextPoll();
    });
};

export const taskPollingService = { pollTask, listenToTask };
